/**
 * Restaurant POS — paginated stock fetch helpers.
 *
 * Ticket #26135 / AB#26135
 * Intent: Mirror Retail POS App `src/store/modules/stock/stockmodule.js`
 * (`fetchStockPage` / Phase-2 loop, size=1000) so full catalogue sync uses
 * `GET /stock/{DB}/page?page=&size=` instead of the legacy bulk
 * `GET /stock/{DB}` which OOMs/500s on large wholesale catalogues
 * (`StockMinifier.getAllStockV2`).
 *
 * AB#5549 / Feature AB#5548 — parallel full-sync page pool:
 * Port Retail `runBackgroundStockPages` bounded concurrency (desktop 4 /
 * mobile 2) into `accumulateStockPages`. Fetches run in a pool; items are
 * pushed into the accumulator in strict page order (single-writer) so PLU
 * order and mid-error partial policy stay identical to the sequential path.
 *
 * No server change — client-only port.
 *
 * Error policy (matches Retail Phase 2 background pages):
 * - Page 0 failure → throw (caller falls back to cache / errorHandler).
 * - Mid-loop failure after page 0+ succeeded → keep accumulated items,
 *   set `partial: true`, stop looping (do not discard successful pages).
 *
 * CommonJS so unit tests can `require()` without a bundler.
 * docs/design-docs/2026-08-04-ticket-26135-restaurant-stock-pagination-fix.md
 */

/** Server clamps size ≤ 1000; match Retail full-sync page size. */
const STOCK_PAGE_SIZE = 1000;

/**
 * Safety cap: 1000 pages × 1000 items = 1M SKUs max.
 * Prevents runaway loops if the server always returns a full page.
 */
const MAX_STOCK_PAGES = 1000;

/** Per-page HTTP timeout (ms) — same order as Retail STOCK_FETCH_PAGE_TIMEOUT_MS. */
const STOCK_FETCH_PAGE_TIMEOUT_MS = 30000;

/**
 * Desktop / Electron full-sync page-fetch concurrency.
 * Matches Retail STOCK_SYNC_PAGE_CONCURRENCY_DESKTOP (AB#5542).
 * Higher pool shortens cold sync wall-clock on wired/Wi-Fi POS terminals
 * without saturating typical branch backhaul.
 */
const STOCK_SYNC_PAGE_CONCURRENCY_DESKTOP = 4;

/**
 * Native mobile (Capacitor / androidMode) full-sync page-fetch concurrency.
 * Matches Retail STOCK_SYNC_PAGE_CONCURRENCY_MOBILE (AB#5542).
 * Mobile radios and JS heaps handle fewer parallel large JSON payloads cleanly.
 */
const STOCK_SYNC_PAGE_CONCURRENCY_MOBILE = 2;

/**
 * Detect Capacitor native / androidMode so concurrency can drop on handhelds.
 * Mirrors Restaurant stockmodule.js `isNativeMobileRuntime` and Retail helper.
 * Safe when `window` is absent (Node unit tests) — returns false → desktop pool.
 *
 * @returns {boolean} true when running on native mobile shell
 */
function isNativeMobileRuntime() {
    try {
        if (typeof window === 'undefined') {
            return false;
        }
        if (window.androidMode) {
            return true;
        }
        return !!(
            window.Capacitor &&
            typeof window.Capacitor.isNativePlatform === 'function' &&
            window.Capacitor.isNativePlatform()
        );
    } catch (_) {
        // Defensive: broken host globals must not break stock sync scheduling.
        try {
            return !!(typeof window !== 'undefined' && window.androidMode);
        } catch (__) {
            return false;
        }
    }
}

/**
 * Resolve page-fetch concurrency for the current runtime (or an explicit override).
 * Desktop/Electron uses 4; native mobile uses 2. Callers/tests may pass
 * `concurrency` on `accumulateStockPages` to pin a value without probing window.
 *
 * @param {number|undefined|null} override optional explicit pool size
 * @returns {number} max in-flight `/stock/{DB}/page` requests (≥ 1)
 */
function getStockSyncPageConcurrency(override) {
    if (override != null && Number.isFinite(Number(override)) && Number(override) >= 1) {
        return Math.floor(Number(override));
    }
    return isNativeMobileRuntime()
        ? STOCK_SYNC_PAGE_CONCURRENCY_MOBILE
        : STOCK_SYNC_PAGE_CONCURRENCY_DESKTOP;
}

/**
 * Normalize a stock page response body into `{ items }`.
 *
 * Tolerates wire shapes the Online Stock Server has shipped over time:
 * 1. Bare JSON array (legacy)
 * 2. Envelope with `items` (StockPageResponse)
 * 3. Envelope with `data` / `stock` / `rows`
 *
 * @param {*} payload response body from GET /stock/{DB}/page
 * @param {number} [pageSize=STOCK_PAGE_SIZE] unused for parse; kept for call-site parity with Retail
 * @returns {{ items: Array }}
 */
function parseStockPagePayload(payload, pageSize = STOCK_PAGE_SIZE) {
    void pageSize;
    if (Array.isArray(payload)) {
        return { items: payload };
    }

    if (!payload || typeof payload !== 'object') {
        return { items: [] };
    }

    const items =
        (Array.isArray(payload.items) && payload.items) ||
        (Array.isArray(payload.data) && payload.data) ||
        (Array.isArray(payload.stock) && payload.stock) ||
        (Array.isArray(payload.rows) && payload.rows) ||
        [];

    return { items };
}

/**
 * Fetch all stock pages via a bounded concurrent pool, then append items in
 * strict ascending page order (single-writer accumulator).
 *
 * Why parallel fetch + sequential push:
 * - Wall-clock of cold full-sync is dominated by HTTP RTT × page count.
 * - Parallelising fetches cuts latency; serialising the push preserves
 *   deterministic item order and the existing mid-error partial policy
 *   (keep pages already written; do not interleave out-of-order SKUs).
 *
 * Stop conditions (any one ends scheduling + apply):
 * - empty page (items.length === 0)
 * - short page (items.length < pageSize) — catalogue end
 * - safety cap (page index reaches maxPages)
 * - mid-loop fetch error after at least one successful page → partial
 *
 * Page-0 fetch error with an empty accumulator rethrows (caller uses cache).
 * On stop, remaining in-flight HTTP is drained via Promise.allSettled so no
 * orphan requests linger after the function returns.
 *
 * @param {Object} args
 * @param {(page: number, size: number) => Promise<{items?: Array}|Array>} args.fetchPage
 *        Injected page fetcher — production passes axios wrapper; tests pass mocks.
 * @param {number} [args.pageSize=STOCK_PAGE_SIZE]
 * @param {number} [args.maxPages=MAX_STOCK_PAGES]
 * @param {number} [args.concurrency] optional pool size; default desktop 4 / mobile 2
 * @returns {Promise<{ items: Array, partial: boolean, pagesFetched: number, error: Error|null }>}
 */
async function accumulateStockPages({
    fetchPage,
    pageSize = STOCK_PAGE_SIZE,
    maxPages = MAX_STOCK_PAGES,
    concurrency: concurrencyOverride,
} = {}) {
    if (typeof fetchPage !== 'function') {
        throw new Error('[Stock Pagination] fetchPage callback is required');
    }

    const safeSize = Math.max(1, Number(pageSize) || STOCK_PAGE_SIZE);
    const safeMax = Math.max(1, Number(maxPages) || MAX_STOCK_PAGES);
    const concurrency = getStockSyncPageConcurrency(concurrencyOverride);

    const accumulator = [];
    let partial = false;
    let lastError = null;
    let pagesFetched = 0;

    // Exclusive upper bound on page indices we may schedule (0 .. safeMax-1).
    const maxPageExclusive = safeMax;
    let nextPageToStart = 0;
    let nextPageToApply = 0;
    let stopNewFetches = false;
    let applyFinished = false;
    let hitSafetyCap = false;

    /** @type {Map<number, Promise<{pageNum: number, raw: *, error: Error|null}>>} */
    const inflight = new Map();
    /** @type {Map<number, {raw: *, error: Error|null}>} */
    const readyByPage = new Map();

    /**
     * Start one page fetch and track it in the inflight map.
     * Errors are captured into the result object so Promise.race never rejects
     * and the single-writer path can apply the page-0 throw / mid partial policy.
     *
     * @param {number} pageNum zero-based page index
     * @returns {Promise<{pageNum: number, raw: *, error: Error|null}>}
     */
    const startPageFetch = (pageNum) => {
        const task = (async () => {
            try {
                const raw = await fetchPage(pageNum, safeSize);
                return { pageNum, raw, error: null };
            } catch (err) {
                return {
                    pageNum,
                    raw: null,
                    error: err instanceof Error ? err : new Error(String(err)),
                };
            }
        })();
        inflight.set(pageNum, task);
        return task;
    };

    /**
     * Fill the fetch pool up to `concurrency` while scheduling is still open.
     * Does not await — the pump races/joins inflight promises separately.
     */
    const fillFetchPool = () => {
        while (
            !stopNewFetches &&
            !applyFinished &&
            inflight.size < concurrency &&
            nextPageToStart < maxPageExclusive
        ) {
            const pageNum = nextPageToStart;
            nextPageToStart += 1;
            startPageFetch(pageNum);
        }
    };

    fillFetchPool();

    // Pump: wait for fetches, append in strict page order, refill pool.
    while (!applyFinished) {
        if (!readyByPage.has(nextPageToApply)) {
            if (inflight.size === 0) {
                // Nothing left to wait for and next apply page never arrived.
                break;
            }
            // Wait for any in-flight page; park the result or discard if past stop.
            // After a short/empty/error page we keep only results still needed for
            // ordered apply (page === nextPageToApply); higher pages are dropped.
            const settled = await Promise.race(inflight.values());
            inflight.delete(settled.pageNum);
            const pastStop =
                applyFinished || (stopNewFetches && settled.pageNum > nextPageToApply);
            if (!pastStop) {
                readyByPage.set(settled.pageNum, {
                    raw: settled.raw,
                    error: settled.error,
                });
            }
            fillFetchPool();
            continue;
        }

        const entry = readyByPage.get(nextPageToApply);
        readyByPage.delete(nextPageToApply);

        if (entry.error) {
            lastError = entry.error;
            if (accumulator.length > 0) {
                // Mid-loop error: keep pages already written (Retail Phase-2 policy).
                partial = true;
                applyFinished = true;
                stopNewFetches = true;
                readyByPage.clear();
                break;
            }
            // Page-0 (or first successful page never landed) — rethrow for cache fallback.
            stopNewFetches = true;
            applyFinished = true;
            readyByPage.clear();
            // Drain before throw so orphan HTTP does not outlive the failure.
            if (inflight.size > 0) {
                await Promise.allSettled([...inflight.values()]);
                inflight.clear();
            }
            throw entry.error;
        }

        pagesFetched += 1;
        const parsed = parseStockPagePayload(entry.raw, safeSize);
        const items = Array.isArray(parsed.items) ? parsed.items : [];

        if (items.length === 0) {
            // Empty page — catalogue end (or empty store on page 0).
            applyFinished = true;
            stopNewFetches = true;
            readyByPage.clear();
            break;
        }

        // Single-writer push — never parallelise accumulator mutation.
        for (let i = 0; i < items.length; i++) {
            accumulator.push(items[i]);
        }

        if (items.length < safeSize) {
            // Short page ⇒ last page (do not trust hasMore alone).
            applyFinished = true;
            stopNewFetches = true;
            readyByPage.clear();
            break;
        }

        nextPageToApply += 1;
        if (nextPageToApply >= maxPageExclusive) {
            // Would schedule beyond safety cap — stop with what we have.
            hitSafetyCap = true;
            applyFinished = true;
            stopNewFetches = true;
            break;
        }
        fillFetchPool();
    }

    // Drain leftover in-flight HTTP so we do not leave orphan requests after stop.
    if (inflight.size > 0) {
        await Promise.allSettled([...inflight.values()]);
        inflight.clear();
    }

    if (hitSafetyCap && !partial) {
        // Hit safety cap while still receiving full pages — return what we have.
        partial = true;
        if (typeof console !== 'undefined' && console.warn) {
            console.warn(
                `[Stock Pagination] Hit safety cap (${safeMax} pages × ${safeSize}); stopping with ${accumulator.length} items`
            );
        }
    }

    return {
        items: accumulator,
        partial,
        pagesFetched,
        error: lastError,
    };
}

module.exports = {
    STOCK_PAGE_SIZE,
    MAX_STOCK_PAGES,
    STOCK_FETCH_PAGE_TIMEOUT_MS,
    STOCK_SYNC_PAGE_CONCURRENCY_DESKTOP,
    STOCK_SYNC_PAGE_CONCURRENCY_MOBILE,
    isNativeMobileRuntime,
    getStockSyncPageConcurrency,
    parseStockPagePayload,
    accumulateStockPages,
};
